import React, { useState } from 'react';
import { Fingerprint, ArrowRight, Loader2, Mail, Lock } from 'lucide-react';
import { signInWithEmailCode, sendEmailVerificationCode } from '../../services/firebaseAuth';
import { NoAutoFillInput } from '../ui/NoAutoFillInput';

const LoginScreen = ({ onLogin, t }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [comingSoonMsg, setComingSoonMsg] = useState(null);

  const handleConnect = async () => {
    if (loading) return;
    
    if (!email) {
      setError(t.pleaseEnterEmail || '请先输入邮箱地址');
      return;
    }
    
    setLoading(true);
    setError(null);

    if (email === '20251018') {
      try {
        const API_BASE_URL = window.__api_base_url || (process.env.NODE_ENV === 'production' ? 'https://openyc-api-backend.fly.dev' : 'http://localhost:8081');
        console.log('[Login] API_BASE_URL:', API_BASE_URL, 'email:', email);
        const response = await fetch(`${API_BASE_URL}/api/v1/auth/verify-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: '20251018', code: '' })
        });
        const data = await response.json();
        
        if (data.success) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          console.log('✅ Dev login successful');
          onLogin(data.user);
          return;
        } else {
          setError(data.error || '登录失败');
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('❌ Dev login failed:', err);
        setError('登录失败，请重试');
        setLoading(false);
        return;
      }
    }

    try {
      console.log('[Login] Calling sendEmailVerificationCode with email:', email);
      await sendEmailVerificationCode(email);
      setCode('');
      setShowCodeForm(true);
      console.log('✅ Verification code sent');
      setLoading(false);
    } catch (err) {
      console.error('❌ Send code failed:', err);
      console.error('❌ Error type:', typeof err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error stack:', err.stack);
      setError(err.message || '发送验证码失败');
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (loading || !email || !code) return;

    setLoading(true);
    setError(null);
    
    console.log('🔵 [Login] Starting verification...');

    try {
      console.log('🔵 [Login] Calling signInWithEmailCode...');
      const result = await signInWithEmailCode(email, code);
      console.log('✅ [Login] Verification successful:', result);
      
      const authToken = localStorage.getItem('auth_token');
      console.log('🔵 [Login] Auth token exists:', !!authToken);
      
      if (authToken) {
        localStorage.setItem('token', authToken);
      }
      
      const userData = {
        uid: result.user?.uid || result.uid || email,
        email: result.user?.email || email,
        displayName: result.user?.displayName || email,
        photoURL: result.user?.photoURL || null
      };
      
      console.log('🔵 [Login] Saving user data:', userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('🔵 [Login] Calling onLogin...');
      
      try {
        await onLogin(userData);
        console.log('✅ [Login] onLogin completed');
      } catch (loginErr) {
        console.error('❌ [Login] onLogin failed:', loginErr);
        setError('登录处理失败: ' + loginErr.message);
      }
    } catch (err) {
      console.error('❌ [Login] Verification failed:', err);
      setError(err.message || '验证码错误或已过期');
    } finally {
      setLoading(false);
      console.log('🔵 [Login] Loading reset, final state:', { loading: false });
    }
  };

  const handleAppleLogin = async () => {
    setComingSoonMsg(`Apple ${t.comingSoon || 'Coming soon...'}`);
    setTimeout(() => setComingSoonMsg(null), 2000);
  };

  const handleGoogleLogin = async () => {
    setComingSoonMsg(`Google ${t.comingSoon || 'Coming soon...'}`);
    setTimeout(() => setComingSoonMsg(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#000] flex flex-col items-center justify-start pt-10 px-6 overflow-y-auto">
      <div className="liquid-bg-container">
        <div className="liquid-orb orb-1"></div>
        <div className="liquid-orb orb-2"></div>
        <div className="liquid-orb orb-3"></div>
      </div>
      <div className="cyber-grid"></div>

      <div className="w-full max-w-sm relative z-10 flex flex-col gap-10">
        <div className="text-center space-y-6">
          <div className="w-28 h-28 mx-auto rounded-[32px] bg-gradient-to-br from-[var(--neon-primary)] to-blue-900 flex items-center justify-center shadow-[0_0_60px_rgba(0,240,255,0.4)] border border-white/20 p-1.5 relative">
             <div className="w-full h-full bg-black/60 backdrop-blur-xl rounded-[28px] flex items-center justify-center border border-white/10 z-10">
                <Fingerprint size={56} className="text-white drop-shadow-[0_0_10px_rgba(0,242,234,0.8)]" />
             </div>
             <div className="absolute inset-0 bg-[var(--neon-primary)] blur-xl opacity-40 animate-pulse"></div>
          </div>
          <div>
            <h1 className="text-6xl font-black tracking-tighter glitch text-white mb-2 text-glow-cyan" data-text="OPENYC">OPENYC</h1>
          </div>
        </div>

        <div className="glass-card p-6 rounded-[32px] shadow-2xl backdrop-blur-2xl border-white/05 bg-white/05 flex flex-col gap-5">

          {!showCodeForm ? (
            <>
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <NoAutoFillInput
                  type="text"
                  placeholder="邮箱地址"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  name="email"
                  id="email"
                  className="w-full py-3 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:border-[var(--neon-primary)]/50 transition-colors"
                />
                </div>
              </div>

              <button
                onClick={handleConnect}
                className="w-full py-4 rounded-2xl font-black bg-gradient-to-r from-[#00f2ea] to-cyan-500 text-black flex items-center justify-center gap-3 shadow-[0_0_40px_rgba(0,242,234,0.6)] border border-[var(--neon-primary)]/50 hover:scale-[1.02] active:scale-95 transition-all text-lg group relative overflow-hidden"
              >
                {loading ? <Loader2 className="animate-spin text-black" /> : (
                  <>
                    <span className="z-10">{email ? '发送验证码' : t.connectSignal}</span>
                    <ArrowRight size={20} className="text-black z-10 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </>
          ) : (
            <form onSubmit={handleVerifyCode} className="flex flex-col gap-3">
              <div className="text-center mb-2">
                <p className="text-sm text-gray-400">验证码已发送至</p>
                <p className="text-white font-mono">{email}</p>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <NoAutoFillInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="6位验证码"
                  value={code}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value) && e.target.value.length <= 6) {
                      setCode(e.target.value);
                    }
                  }}
                  name="code"
                  id="code"
                  className="w-full py-3 pl-12 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:border-[var(--neon-primary)]/50 transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || !code || code.length !== 6}
                className="w-full py-3 rounded-xl font-bold bg-[var(--neon-primary)] text-black shadow-[0_0_40px_rgba(0,242,234,0.6)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : (
                  <>
                    <Lock size={18} />
                    <span>验证登录</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowCodeForm(false)}
                className="text-[var(--neon-primary)] text-sm hover:underline"
              >
                重新输入邮箱
              </button>
            </form>
          )}

          {error && (
            <div className="text-red-400 text-xs text-center">{error}</div>
          )}

          {comingSoonMsg && (
            <div className="text-[var(--neon-primary)] text-xs text-center animate-pulse">{comingSoonMsg}</div>
          )}

          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-center gap-4">
              <button onClick={handleGoogleLogin} className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all backdrop-blur-md group" title="Google">
                <div className="opacity-80 group-hover:opacity-100 transition-opacity">
                  <svg viewBox="0 0 24 24" width="20" height="20"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                </div>
              </button>
              <button onClick={handleAppleLogin} className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all backdrop-blur-md group" title="Apple">
                <div className="opacity-80 group-hover:opacity-100 transition-opacity">
                  <svg viewBox="0 0 384 512" width="20" height="20"><path fill="white" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z"/></svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
